import React, { useState } from "react";
import { TableProps } from "antd/es/table";
import { IApplicant, IOnProjectSelect, IProjectDetails } from "./interfaces";
import { FilePdfOutlined, EyeOutlined, MessageOutlined } from "@ant-design/icons";
import EditableStatus from "../applicantsPanel/editableStatus";
import { startCase } from "lodash";
import { StatusType, formatInterviewDateTime } from "../../../Components/common";
import { ChatBadge } from "../../../Components/common/ChatBadge";
import RemarksView from "../applicantsPanel/RemarksView";
import { Button, Dropdown, Menu, Modal, Input, Space, Badge, Tooltip, Tag } from "antd";
import { IProjectDetails as IProjectDetailsHelper } from "../helper";
import EditablePriority from "../../RecruitersPanel/projectsPanel/editablePriority";

// Helper function to format status for display
const formatStatus = (status: string): { label: string; color: string } => {
  const statusMap: { [key: string]: { label: string; color: string } } = {
    'pending': { label: 'Pending', color: 'default' },
    'submitted': { label: 'Submitted', color: 'blue' },
    'under_review': { label: 'Under Review', color: 'processing' },
    'shortlisted': { label: 'Shortlisted', color: 'cyan' },
    'shortlisted_manager': { label: 'Shortlisted by Manager', color: 'cyan' },
    'shortlisted_by_manager': { label: 'Shortlisted by Manager', color: 'cyan' },
    'shortlisted_recruiter': { label: 'Shortlisted by Recruiter', color: 'cyan' },
    'shortlisted_by_recruiter': { label: 'Shortlisted by Recruiter', color: 'cyan' },
    'interviewing': { label: 'Interviewing', color: 'purple' },
    'interviewing_manager': { label: 'Interview with Manager', color: 'purple' },
    'interview_scheduled': { label: 'Interview Scheduled', color: 'geekblue' },
    'offer_sent': { label: 'Offer Sent', color: 'orange' },
    'offer_accepted': { label: 'Offer Accepted', color: 'success' },
    'offer_rejected': { label: 'Offer Rejected', color: 'error' },
    'hired': { label: 'Hired', color: 'success' },
    'rejected': { label: 'Rejected', color: 'error' },
    'rejected_manager': { label: 'Rejected by Manager', color: 'error' },
    'rejected_by_manager': { label: 'Rejected by Manager', color: 'error' },
    'rejected_recruiter': { label: 'Rejected by Recruiter', color: 'error' },
    'rejected_by_recruiter': { label: 'Rejected by Recruiter', color: 'error' },
    'approved_manager': { label: 'Approved by Manager', color: 'success' },
    'approved_by_manager': { label: 'Approved by Manager', color: 'success' },
    'withdrawn': { label: 'Withdrawn', color: 'default' },
    'on_hold': { label: 'On Hold', color: 'warning' },
  };
  
  if (statusMap[status]) {
    return statusMap[status];
  }
  
  // Fallback: convert snake_case to Title Case
  const label = status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
  
  return { label, color: 'default' };
};

export const toHHMMSS = (val = 0) => {
  const hours = Math.floor(val / 3600);
  const minutes = Math.floor((val % 3600) / 60);
  let output = "";
  if (hours) output += `${hours} hr `;
  if (minutes) output += `${minutes} mins `;
  return output;
};

export const ApplicantsColumns = (
  refetch: () => void,
  onStatusChange: (id: string, status: string, feedback?: string) => void,
  openProfileDrawer: (applicant: IApplicant) => void,
  openRemarks: (jobApplicationId: string) => void,
  openFeedback: (id: string, feedback: string) => void,
  setViewOfferModal: React.Dispatch<React.SetStateAction<{ visible: boolean; record?: IApplicant }>>,
  unreadCounts?: { [id: string]: number },
  handleApprove?: (applicationId: string) => void,
  handleReject?: (applicationId: string) => void,
): TableProps<IApplicant>["columns"] => [
  {
    title: <span style={{ fontSize: '18px', fontWeight: 600 }}>Name</span>,
    dataIndex: "name",
    key: "name",
    render: (text: string) => <span style={{ fontSize: '16px', fontWeight: 500 }}>{text}</span>,
  },
 
  {
    title: <span style={{ fontSize: '18px', fontWeight: 600 }}>Meeting Type</span>,
    dataIndex: "secondInterviewType",
    key: "secondInterviewType",
    align: "center",
    render: (_, { secondInterviewType }) => (
      <span style={{ fontSize: '16px' }}>{secondInterviewType?.split('(')[0] ?? "N/A"}</span>
    ),
  },
  {
    title: <span style={{ fontSize: '18px', fontWeight: 600 }}>Interview Date</span>,
    dataIndex: "secondInterviewDate",
    key: "secondInterviewDate",
    align: "center",
    render: (_, { secondInterviewDate, secondInterviewLink }) => (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
        <span style={{ fontSize: '16px' }}>
          {secondInterviewDate && secondInterviewDate !== "N/A"
            ? formatInterviewDateTime(secondInterviewDate)
            : secondInterviewDate}
        </span>
        {secondInterviewLink && (
          <a
            href={secondInterviewLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: '14px', fontWeight: 600 }}
          >
            Join interview
          </a>
        )}
      </div>
    ),
  },
  {
    title: <span style={{ fontSize: '18px', fontWeight: 600 }}>Status</span>,
    dataIndex: "status",
    key: "status",
    align: "center",
    render: (_, record) => {
      if (record.status === "pending" || record.status === "submitted") {
        return (
          <EditableStatus
            id={record._id}
            status={record.status as StatusType}
            onUpdate={() => refetch()}
          />
        );
      }
      const { label, color } = formatStatus(record.status || '');
      return (
        <Tag 
          color={color} 
          style={{ 
            fontSize: '14px', 
            padding: '4px 12px',
            borderRadius: '4px',
          }}
        >
          {label}
        </Tag>
      );
    },
  },
  {
    title: <span style={{ fontSize: '18px', fontWeight: 600 }}>Profile</span>,
    key: "profile",
    align: "center",
    render: (_, record) => (
      <Button 
        icon={<EyeOutlined />} 
        onClick={() => openProfileDrawer(record)}
        style={{ fontSize: '14px', height: '36px', padding: '0 16px' }}
      >
        View Profile
      </Button>
    ),
  },
  {
    title: <span style={{ fontSize: '18px', fontWeight: 600 }}>Remarks</span>,
    dataIndex: "remarks",
    align: "center",
    render: (_, record) => {
      const unreadCount = unreadCounts?.[record.jobApplicationId || ""] || 0;
      return (
        <Tooltip title={`Chat with recruiter${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}>
          <ChatBadge count={unreadCount} offset={[-8, 8]}>
            <MessageOutlined
              style={{ fontSize: 22, color: "#002140", cursor: "pointer" }}
              onClick={() => openRemarks(record.jobApplicationId || "")}
            />
          </ChatBadge>
        </Tooltip>
      );
    },
  },
  {
    title: <span style={{ fontSize: '18px', fontWeight: 600 }}>Actions</span>,
    key: 'actions',
    align: 'center',
    render: (_: any, record: IApplicant) => {
      if (record.status !== 'interviewing_manager') return null;
      return (
        <Space>
          <Button
            type="primary"
            style={{ background: '#52c41a', borderColor: '#52c41a', fontSize: '14px', height: '36px' }}
            onClick={() => handleApprove?.(record._id)}
          >
            Approve
          </Button>
          <Button
            danger
            style={{ fontSize: '14px', height: '36px' }}
            onClick={() => handleReject?.(record._id)}
          >
            Reject
          </Button>
        </Space>
      );
    },
  },
  {
    title: <span style={{ fontSize: '18px', fontWeight: 600 }}>Offer</span>,
    key: 'offer',
    render: (_: any, record: IApplicant) => {
      if (!record.offerLetter) return <span style={{ fontSize: '16px' }}>—</span>;
      let buttonType: "default" | "link" | "text" | "primary" | "dashed" | undefined = "default";
      let buttonStyle: React.CSSProperties = { fontSize: '14px', height: '36px', padding: '0 16px' };
      if (record.offerAccepted) {
        buttonType = "primary";
        buttonStyle = { ...buttonStyle, backgroundColor: '#52c41a', borderColor: '#52c41a' };
      } else if (record.offerRejected) {
        buttonType = "primary";
        buttonStyle = { ...buttonStyle, backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' };
      }
      return (
        <Button
          type={buttonType}
          style={buttonStyle}
          onClick={() => setViewOfferModal && setViewOfferModal({ visible: true, record })}
        >
          View Offer
        </Button>
      );
    }
  },
];

export const ProjectsColumns = (
  onSelect: IOnProjectSelect
): TableProps<IProjectDetails>["columns"] => [
  {
    title: <span style={{ fontSize: '18px', fontWeight: 600 }}>Company Name</span>,
    dataIndex: "name",
    key: "name",
    render: (_, { name, _id }) => (
      <b
        onClick={() => onSelect(_id)}
        style={{ cursor: "pointer", color: "#00e", fontSize: '16px', fontWeight: 500 }}
      >
        {name}
      </b>
    ),
  },
  {
    title: <span style={{ fontSize: '18px', fontWeight: 600 }}>Position Title</span>,
    dataIndex: "positionTitle",
    key: "positionTitle",
    render: (text: string) => <span style={{ fontSize: '16px' }}>{text || '-'}</span>,
  },
  {
    title: <span style={{ fontSize: '18px', fontWeight: 600 }}>Recruiter</span>,
    dataIndex: "recruiterName",
    key: "recruiterName",
    render: (text: string) => <span style={{ fontSize: '16px' }}>{text || '-'}</span>,
  },
  {
    title: <span style={{ fontSize: '18px', fontWeight: 600 }}>Total Applicants</span>,
    dataIndex: "applicantsCount",
    key: "applicantsCount",
    align: "center" as const,
    render: (_, { applicantsCount }) => <span style={{ fontSize: '16px' }}>{applicantsCount || 0}</span>,
  },
];

export const getStatusList = (statusTypeArr?: Array<string>) => {
  const elems = statusTypeArr || Object.values(StatusType);
  return elems.map((value) => ({
    label: startCase(value.toLowerCase()),
    value: value as StatusType,
  }));
};
